import type { ListingsAPIAndFilter, ListingsAPIOrFilter } from '@noths/polaris-client-listings-api';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { ListingsAPIServingConfig } from 'src/services/listings-api/servingConfigs';
import type { Facet } from './types/Facet';
import type { SortOption } from './types/SortOption';

export interface ProductsQueryState {
  canonicalFilters?: ListingsAPIAndFilter | ListingsAPIOrFilter;
  facets: Facet[];
  isModalOpen: boolean;
  page: number;
  pageCategories?: string[];
  searchTerm?: string;
  servingConfig?: ListingsAPIServingConfig;
  sortOptions: SortOption[];
}

export interface SyncProductsQueryFromURLPayload {
  activeFacetValuesByKey: Record<string, string[]>;
  activeSort: { descending: boolean; key: string } | null;
  page: number;
  searchTerm?: string;
}

export const initialState: Readonly<ProductsQueryState> = {
  facets: [],
  isModalOpen: false,
  page: 1,
  sortOptions: [],
};

const applyActiveFacetValuesToFacets = (
  facets: Facet[],
  activeFacetValuesByKey: Record<string, string[]>,
): Facet[] =>
  facets.map((facet) => {
    const activeValues = new Set(activeFacetValuesByKey[facet.key] ?? []);

    return {
      ...facet,
      options: facet.options.map((option) => ({
        ...option,
        active: activeValues.has(option.value),
      })),
    };
  });

const applyActiveSortToSortOptions = (
  sortOptions: SortOption[],
  activeSort: SyncProductsQueryFromURLPayload['activeSort'],
): SortOption[] =>
  sortOptions.map((option) => ({
    ...option,
    active:
      activeSort != null &&
      option.key === activeSort.key &&
      option.descending === activeSort.descending,
  }));

export const { actions, reducer } = createSlice({
  name: 'productsQuery',
  initialState,
  reducers: {
    closeModal: (state) => {
      state.isModalOpen = false;
    },
    openModal: (state) => {
      state.isModalOpen = true;
    },
    changeSortOption: (state, action: PayloadAction<string>) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        if (!action.payload) {
          return {
            ...option,
            active: false,
          };
        }
        const [key, direction] = action.payload.split('-');
        const isDescending = direction === 'desc';

        return {
          ...option,
          active: option.key === key && option.descending === isDescending,
        };
      });
    },
    resetSortOptions: (state) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        return {
          ...option,
          active: option.key === '',
        };
      });
    },
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    syncFromURL: (state, action: PayloadAction<SyncProductsQueryFromURLPayload>) => {
      const { activeFacetValuesByKey, activeSort, page, searchTerm } = action.payload;

      state.facets = applyActiveFacetValuesToFacets(state.facets, activeFacetValuesByKey);
      state.sortOptions = applyActiveSortToSortOptions(state.sortOptions, activeSort);
      state.page = page;
      state.searchTerm = searchTerm;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(requestProducts.fulfilled, (state, { payload }) => {
      if (payload.sortOptions) {
        state.sortOptions = payload.sortOptions;
      }

      if (payload.facets) {
        state.facets = payload.facets;
      }
    });
  },
});
